import styled from "styled-components";
import { animate, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import {
  faFile,
  faLocationPin,
  faArrowDown,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import TextType from "./TextType";
import { useRef, useState } from "react";
import { COLOURS } from "../theme";
import AboutPanel from "./AboutPanel";

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const PFP = `${PUBLIC_URL}/favicons/pfp.jpeg`;

const Intro = styled(motion.div)`
  max-width: 680px;
  text-align: left;
`;

const H1 = styled.h1`
  font-size: 60px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${COLOURS.textStrong};

  @media (max-width: 768px) {
    font-size: 40px;
    text-align: center;
  }
  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const Location = styled.p`
  font-size: 18px;
  color: #94a3b8;
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const Lead = styled.p`
  font-size: 30px;
  font-weight: 500;
  color: ${COLOURS.textStrong};
  margin-bottom: 20px;
  @media (max-width: 768px) {
    font-size: 22px;
    text-align: center;
  }
`;

const Details = styled.p`
  font-size: 20px;
  line-height: 1.7;
  color: ${COLOURS.text};
  margin-bottom: 28px;
  @media (max-width: 768px) {
    font-size: 16px;
    text-align: center;
  }
`;

const SocialNav = styled.nav`
  display: flex;
  gap: 22px;
  font-size: 25px;

  @media (min-width: 769px) {
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }

  a,
  button {
    color: ${COLOURS.text};
    transition: color 0.25s ease;
    background: none;
    border: none;
    cursor: pointer;

    display: flex;
    align-items: center;

    &:hover,
    &:focus-visible {
      color: ${COLOURS.accent};
      outline: none;
    }
  }
`;

const IconButton = styled.button`
  font-size: 25px;
  padding: 0;
`;

const ProfileImage = styled(motion.img)`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  border: 2px solid ${COLOURS.white};
  object-fit: cover;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const ArrowWrap = styled(motion.div)`
  cursor: pointer;
  font-size: 32px;
  color: ${COLOURS.text};
  transition: color 0.25s ease;

  &:hover,
  &:focus-visible {
    color: ${COLOURS.accent};
    outline: none;
  }
`;

const Hero = () => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [aboutOpen, setAboutOpen] = useState(false);

  const scrollToProjects = () => {
    const el = document.getElementById("projects");
    if (!el) return;

    const targetY = el.getBoundingClientRect().top + window.scrollY;

    animate(window.scrollY, targetY, {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
      onUpdate: (latest) => window.scrollTo(0, latest),
    });
  };

  return (
    <Section>
      <ContentRow>
        <div aria-label="Profile picture" tabIndex={0}>
          <ProfileImage
            ref={imgRef}
            src={PFP}
            alt="Amy Jordan"
            animate={{ scale: 1 }}
            transition={{ duration: 0.35 }}
          />
        </div>

        <Intro>
          <H1>
            <TextType
              text={["Hi, I'm Amy!"]}
              typingSpeed={100}
              deletingSpeed={100}
              pauseDuration={1500}
              showCursor
              cursorCharacter="|"
              initialDelay={500}
            />
          </H1>

          <Location>
            <FontAwesomeIcon icon={faLocationPin} aria-hidden="true" /> Suffolk,
            England
          </Location>

          <Lead>Front-end Software Engineer @ BT Group</Lead>

          <Details>
            On my <strong>GitHub</strong>, you’ll find code from both academic
            and personal projects. My <strong>LinkedIn</strong> highlights my
            professional journey, including my apprenticeship at BT.
          </Details>

          <SocialNav aria-label="Social links">
            <a
              href="https://github.com/amylouisejordan"
              aria-label="GitHub profile"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faGithub} aria-hidden="true" />
            </a>

            <a
              href="https://www.linkedin.com/in/amy-jordan333/"
              aria-label="LinkedIn profile"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faLinkedin} aria-hidden="true" />
            </a>

            <IconButton
              aria-label="About Me"
              onClick={() => setAboutOpen(true)}
            >
              <FontAwesomeIcon icon={faUser} aria-hidden="true" />
            </IconButton>
          </SocialNav>

          <AboutPanel open={aboutOpen} onClose={() => setAboutOpen(false)} />
        </Intro>
      </ContentRow>

      <ArrowWrap
        animate={{ y: [0, -10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        onClick={scrollToProjects}
      >
        <FontAwesomeIcon
          icon={faArrowDown}
          aria-label="Scroll to projects section"
        />
      </ArrowWrap>
    </Section>
  );
};

export default Hero;
